import type { ButtonProps } from '@apollo/orbit';
import { Button } from '@apollo/orbit';
import { captureException } from '@sentry/react';
import React, { useState } from 'react';

import { HUBSPOT_FORM_IDS, useHubSpot } from 'src/hooks/useHubSpot';
import { getTrackingCookies } from 'src/lib/analytics';

type Props = Pick<ButtonProps, 'variant' | 'children' | 'size'> & {
  onSuccess?: () => void;
  accountId: string;
  userId: string;
  userEmail: string;
  userFullName: string;
  className?: string;
  phone?: string;
  message: string;
};

/**
 * Instantly pushes lead to HubSpot & shows modal on success
 */
export const HubspotUpgradeButton = ({
  variant,
  accountId,
  children = 'Contact us to upgrade',
  size,
  onSuccess,
  userId,
  userEmail,
  userFullName,
  phone,
  message,
  className,
}: Props) => {
  const pushHubSpotLead = useHubSpot();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    const { referrerCookie, mediumCookie, campaignCookie, sourceCookie } =
      getTrackingCookies();

    setLoading(true);

    // eslint-disable-next-line react-hooks/todo -- fix to unblock React Compiler compilation
    try {
      const success = await pushHubSpotLead({
        formId: HUBSPOT_FORM_IDS.UPGRADE_CONTACT,
        email: userEmail,
        fullName: userFullName,
        phone,
        message,
        userId,
        orgId: accountId,
        utm: {
          referrer: referrerCookie,
          utmSource: sourceCookie,
          utmCampaign: campaignCookie,
          utmMedium: mediumCookie,
        },
      });

      if (success) {
        onSuccess?.();
      }
    } catch (err) {
      captureException(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      isLoading={loading}
      loadingText="Submitting request"
      onClick={handleClick}
      className={className}
      isDisabled={!message}
    >
      {children}
    </Button>
  );
};
